// client/src/coms/views/canvas_parts/canvas_room_view_parts/strokeInput.js
//
// Shared pointer-input processing for the 2D GPU canvas and the 3D model
// viewport. Everything here is pure (no DOM, no WebGL) so both renderers can
// share identical stroke behaviour and the logic can be unit tested.

// A pen that reports contact with zero force (common on the first pointerdown
// sample from Windows Ink and some Wacom drivers) must produce the thinnest
// possible mark, never a full-pressure blob.
export const PEN_MINIMUM_PRESSURE = 0.04;

export function clampUnit(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(1, n));
}

export function isPenPointer(event) {
  return String(event && event.pointerType ? event.pointerType : '').toLowerCase() === 'pen';
}

// Mouse and touch have no meaningful pressure (mouse reports 0.5 while a
// button is held), so they always draw at full pressure.
export function readPointerPressure(event) {
  if (!isPenPointer(event)) return 1;
  const raw = Number(event && event.pressure);
  if (!Number.isFinite(raw)) return 1;
  return Math.max(PEN_MINIMUM_PRESSURE, Math.min(1, raw));
}

// gamma < 1 makes light pressure count for more (softer feel), gamma > 1
// requires a firmer press to reach full size.
export function applyPressureCurve(pressure, gamma = 1) {
  const safePressure = clampUnit(pressure, 1);
  const safeGamma = Number.isFinite(Number(gamma)) && Number(gamma) > 0 ? Number(gamma) : 1;
  if (safeGamma === 1) return safePressure;
  return Math.pow(safePressure, safeGamma);
}

// Hardware pressure is noisy. A light exponential filter removes the
// sample-to-sample flicker that shows up as a beaded edge, without
// noticeably delaying deliberate pressure changes.
export function createPressureSmoother(amount = 0.35) {
  const keep = Math.max(0, Math.min(0.95, Number(amount) || 0));
  let current = null;
  return {
    next(pressure) {
      const value = clampUnit(pressure, 1);
      current = current == null ? value : (current * keep) + (value * (1 - keep));
      return current;
    },
    reset() {
      current = null;
    },
  };
}

// One Euro filter (Casiez et al.). It smooths heavily at low speed, which
// removes hand and sensor jitter during slow precise work, and almost not at
// all at high speed, so fast strokes do not trail behind the cursor.
// Feed it screen-space (CSS pixel) coordinates so the feel does not change
// with canvas zoom. The defaults keep the visible lag at roughly 2-4 px for
// normal drawing speeds; the high derivative cutoff lets it recognise a fast
// stroke within one or two samples.
function smoothingFactor(elapsedSeconds, cutoff) {
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + (tau / elapsedSeconds));
}

export function createPointStabiliser(options = {}) {
  const minCutoff = Math.max(0.01, Number(options.minCutoff) || 3);
  const beta = Math.max(0, Number.isFinite(Number(options.beta)) ? Number(options.beta) : 0.04);
  const derivativeCutoff = Math.max(0.01, Number(options.derivativeCutoff) || 8);
  let previous = null;

  return {
    next(point, timeMs) {
      const x = Number(point && point.x);
      const y = Number(point && point.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return point;
      const time = Number.isFinite(Number(timeMs)) ? Number(timeMs) : 0;

      if (!previous) {
        previous = { x, y, dx: 0, dy: 0, time };
        return { ...point, x, y };
      }

      // Coalesced events can share a timestamp; treat them as one 1 kHz step
      // instead of dividing by zero.
      const elapsedSeconds = Math.max(0.001, (time - previous.time) / 1000);
      const derivativeAlpha = smoothingFactor(elapsedSeconds, derivativeCutoff);
      const rawDx = (x - previous.x) / elapsedSeconds;
      const rawDy = (y - previous.y) / elapsedSeconds;
      const dx = previous.dx + (derivativeAlpha * (rawDx - previous.dx));
      const dy = previous.dy + (derivativeAlpha * (rawDy - previous.dy));
      const speed = Math.hypot(dx, dy);
      const cutoff = minCutoff + (beta * speed);
      const alpha = smoothingFactor(elapsedSeconds, cutoff);
      const filteredX = previous.x + (alpha * (x - previous.x));
      const filteredY = previous.y + (alpha * (y - previous.y));
      previous = { x: filteredX, y: filteredY, dx, dy, time };
      return { ...point, x: filteredX, y: filteredY };
    },
    reset() {
      previous = null;
    },
  };
}

const INTERPOLATED_POINT_KEYS = ['pressure', 'tiltX', 'tiltY', 'twist', 'altitudeAngle', 'azimuthAngle'];

export function interpolateStrokePoint(a, b, t) {
  const safeT = Math.max(0, Math.min(1, Number(t) || 0));
  const point = { ...(safeT < 0.5 ? a : b) };
  point.x = Number(a.x) + ((Number(b.x) - Number(a.x)) * safeT);
  point.y = Number(a.y) + ((Number(b.y) - Number(a.y)) * safeT);
  INTERPOLATED_POINT_KEYS.forEach((key) => {
    const valueA = Number(a[key]);
    const valueB = Number(b[key]);
    if (!Number.isFinite(valueA) && !Number.isFinite(valueB)) return;
    const safeA = Number.isFinite(valueA) ? valueA : valueB;
    const safeB = Number.isFinite(valueB) ? valueB : safeA;
    point[key] = safeA + ((safeB - safeA) * safeT);
  });
  return point;
}

export function strokeMidpoint(a, b) {
  return interpolateStrokePoint(a, b, 0.5);
}

function quadraticPoint(start, control, end, t) {
  return interpolateStrokePoint(
    interpolateStrokePoint(start, control, t),
    interpolateStrokePoint(control, end, t),
    t,
  );
}

// Places dabs at an exact arc-length spacing along a polyline. The distance
// left over at the end of one segment is carried into the next call, so the
// spacing stays even regardless of how the input is split into events. This
// is what prevents gaps on fast strokes and clumps on slow ones.
export function createDabWalker() {
  let distanceToNextDab = 0;
  let hasEmitted = false;

  const walkLine = (a, b, getSpacing, emit) => {
    const ax = Number(a.x);
    const ay = Number(a.y);
    const segmentLength = Math.hypot(Number(b.x) - ax, Number(b.y) - ay);
    if (!hasEmitted) {
      emit(a);
      hasEmitted = true;
      distanceToNextDab = Math.max(0.05, Number(getSpacing(a)) || 1);
    }
    if (segmentLength <= 1e-6) return 0;

    let travelled = 0;
    let emitted = 0;
    // Hard upper bound protects against a pathological spacing of ~0.
    const maximumDabs = 100000;
    while (travelled + distanceToNextDab <= segmentLength && emitted < maximumDabs) {
      travelled += distanceToNextDab;
      const dab = interpolateStrokePoint(a, b, travelled / segmentLength);
      emit(dab);
      emitted += 1;
      distanceToNextDab = Math.max(0.05, Number(getSpacing(dab)) || 1);
    }
    distanceToNextDab -= (segmentLength - travelled);
    return emitted;
  };

  return {
    line(a, b, getSpacing, emit) {
      if (!a || !b) return 0;
      return walkLine(a, b, getSpacing, emit);
    },
    // Flattens the quadratic into short chords (sub-pixel error for normal
    // brush sizes) and walks them as one continuous path.
    quadratic(start, control, end, getSpacing, emit) {
      if (!start || !control || !end) return 0;
      const approximateLength = Math.hypot(Number(control.x) - Number(start.x), Number(control.y) - Number(start.y))
        + Math.hypot(Number(end.x) - Number(control.x), Number(end.y) - Number(control.y));
      const steps = Math.max(1, Math.min(64, Math.ceil(approximateLength / 2)));
      let previous = start;
      let emitted = 0;
      for (let step = 1; step <= steps; step += 1) {
        const next = quadraticPoint(start, control, end, step / steps);
        emitted += walkLine(previous, next, getSpacing, emit);
        previous = next;
      }
      return emitted;
    },
    reset() {
      distanceToNextDab = 0;
      hasEmitted = false;
    },
  };
}

// Midpoint-quadratic stroke builder used by every round brush in the
// product. Each new input point p(n) finalises the curve from
// mid(p(n-2), p(n-1)) to mid(p(n-1), p(n)) with p(n-1) as the control point.
// The curve passes smoothly through every midpoint, so corners are rounded
// without the cursor lag of a stabiliser. finish() draws the last half
// segment to the real final input point so a stroke ends exactly where the
// pen lifted.
export function createCurveStrokeBuilder() {
  const walker = createDabWalker();
  let previousPrevious = null;
  let previous = null;

  return {
    begin(point, getSpacing, emit) {
      walker.reset();
      previousPrevious = null;
      previous = point;
      walker.line(point, point, getSpacing, emit);
    },
    add(point, getSpacing, emit) {
      if (!previous) {
        this.begin(point, getSpacing, emit);
        return;
      }
      if (!previousPrevious) {
        walker.line(previous, strokeMidpoint(previous, point), getSpacing, emit);
      } else {
        walker.quadratic(
          strokeMidpoint(previousPrevious, previous),
          previous,
          strokeMidpoint(previous, point),
          getSpacing,
          emit,
        );
      }
      previousPrevious = previous;
      previous = point;
    },
    finish(getSpacing, emit) {
      if (previous && previousPrevious) {
        walker.line(strokeMidpoint(previousPrevious, previous), previous, getSpacing, emit);
      }
      previousPrevious = null;
      previous = null;
    },
    get lastPoint() {
      return previous;
    },
  };
}

// Per-dab opacity for a soft dab stamped every `spacingRatio` diameters so
// that the overlapping dabs build to `targetCentreAlpha` in the stroke core.
// Using full-alpha soft dabs instead would turn every soft stroke solid.
export function getCompensatedDabFlow(spacingRatio, targetCentreAlpha = 0.985) {
  const ratio = Math.max(0.01, Math.min(1, Number(spacingRatio) || 0.1));
  const target = Math.max(0.01, Math.min(0.999, Number(targetCentreAlpha) || 0.985));
  return 1 - Math.pow(1 - target, ratio);
}
